import { GroupWrapper } from '@dealstack/frontend/shared/components/fundament/GroupWrapper/GroupWrapper';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useKeyPress } from '@dealstack/frontend/shared/other/react/hooks/useKeyPress';
import { useEffect, useRef, useState } from 'react';
import { useController } from 'react-hook-form';
import styled from 'styled-components';

import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';
import type { IStringInputProps } from './StringInput.types';

const StringInput = ({
	className,
	name,
	hint,
	placeholder,
	label,
	'data-testid': dataTestId, // Destructure and rename here
	inputType,
	tooltip,
	iconSize,
	prefixIcon,
	suffixIcon,
	icon,
	disabled,
	rhfOptions,
	onEnterConfirm,
	autocompleteOff,
	inputMaxLength,
	blockRedundantSpaces,
	inputLeftPlaceHolder,
	inputRightPlaceHolder,
}: IStringInputProps) => {
	const [isFocused, setIsFocused] = useState(false);
	const textInputRef = useRef();

	const isPrefixPredefinedIcon = typeof prefixIcon === 'string';
	const isSuffixPredefinedIcon = typeof suffixIcon === 'string';

	const {
		field,
		fieldState: { error },
	} = useController({ name, rules: rhfOptions });

	const onEnter = useKeyPress('Enter', textInputRef?.current);
	useEffect(() => {
		if (!onEnter || !textInputRef?.current) {
			return;
		}
		if (onEnterConfirm) {
			onEnterConfirm();
			(textInputRef?.current as HTMLElement)?.blur();
		}
	}, [onEnter]);

	const renderPrefixIcon = () => {
		if (!prefixIcon) {
			return null;
		}
		if (isPrefixPredefinedIcon) {
			return <PrefixIcon size={iconSize} variant={prefixIcon as PredefinedIcons} />;
		}
		return prefixIcon;
	};

	const renderSuffixIcon = () => {
		if (!suffixIcon) {
			return null;
		}
		if (isSuffixPredefinedIcon) {
			return <SuffixIcon size={iconSize} variant={suffixIcon as PredefinedIcons} />;
		}
		return suffixIcon;
	};

	return (
		<div className={className}>
			<GroupWrapper label={label} hint={hint} tooltip={tooltip} errors={error}>
				<InputWrapper
					isError={!!error}
					onFocus={() => setIsFocused(true)}
					onBlur={() => {
						setTimeout(() => {
							/**
							 * This is a hack to not have memory leak when
							 * we unmounted StringInput below 200ms
							 */
							if (!textInputRef.current) {
								return;
							}
							/**
							 * This is needed because of Enter functionality.
							 * When you click on Enter icon, then we need to lose focus on main Input
							 */
							setIsFocused(false);
						}, 200);
					}}
				>
					{inputLeftPlaceHolder}
					{icon ? <LeftIcon disabled={disabled} variant={icon} /> : null}
					{renderPrefixIcon()}
					<input
						data-testid={dataTestId || `input-string-${name}`}
						disabled={disabled}
						size={onEnterConfirm ? 14 : 21}
						{...field}
						onChange={(event) => {
							const value = event.target.value;
							if (!blockRedundantSpaces) {
								return field.onChange(value);
							}

							//d.zghinnik 24.01.2023 MIP-226
							//replace multiple spaces with one, remove first space
							const preparedValue = value.replace(/\s\s+/g, ' ').replace(/^\s/, '');
							field.onChange(preparedValue);
						}}
						type={inputType}
						placeholder={placeholder}
						ref={(e) => {
							field.ref(e);
							// @ts-expect-error it works
							textInputRef.current = e;
						}}
						autoComplete={autocompleteOff ? 'new-password' : ''}
						maxLength={inputMaxLength}
					/>
					{renderSuffixIcon()}
					{onEnterConfirm && isFocused ? (
						<EnterIcon variant="enter" onClick={() => onEnterConfirm()} />
					) : null}
					{inputRightPlaceHolder}
				</InputWrapper>
			</GroupWrapper>
		</div>
	);
};

const EnterIcon = styled(Icon)`
	position: absolute;
	&.svg {
		--size: 2rem;
	}
`;
export const LeftIcon = styled(Icon)`
	position: absolute;
	&.svg {
		--size: 1rem;
	}
`;

export const PrefixIcon = styled(Icon)`
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	&.svg {
		--size: 1rem;
	}
`;
export const SuffixIcon = styled(Icon)`
	position: absolute;
	right: 1rem;
	top: 50%;
	transform: translateY(-50%);
	&.svg {
		--size: 1rem;
	}
`;

export const InputWrapper = styled.div<{
	isError?: boolean;
}>`
	position: relative;
	width: fit-content;

	${LeftIcon}, ${EnterIcon} {
		cursor: pointer;
	}

	${({ theme, isError }) =>
		isError
			? `
        input {
            border-color: ${theme.v2.color.error[1000]} !important;
        }
    `
			: ``}

	${LeftIcon} {
		top: 50%;
		left: 0;
		transform: translate(50%, -50%);
		z-index: 1;
		background-color: ${({ theme }) => theme.v2.color.white};
	}

	${EnterIcon} {
		top: 50%;
		right: 0;
		transform: translate(-50%, -50%);

		&:active {
			transform: scale(0.95) translate(-50%, -50%);
			transition: transform 0.05s linear;
		}
	}
`;

const Styled = styled(StringInput)`
	display: flex;
	flex-direction: column;

	--placeholder-color: ${({ theme }) => theme.v2.color.neutral[500]};
	--text-color: ${({ theme }) => theme.v2.color.neutral[1100]};
	--border-color: ${({ theme }) => theme.v2.color.neutral[500]};
	--border-color-focused: ${({ theme }) => theme.v2.color.neutral[700]};

	${({ maxSize }) => (maxSize ? 'width: 100%;' : ``)}

	input {
		border: 1px solid var(--border-color);
		border-radius: 4px;
		padding: 0.6rem;
		font-size: 0.75rem;

		${({ icon }) =>
			icon
				? `
            text-indent: 1.3rem;
        `
				: ``}

		${({ onEnterConfirm }) =>
			onEnterConfirm
				? `
            padding-right: 4rem;
        `
				: ``}

    ${({ theme, disabled }) =>
			disabled
				? `
                --text-color: ${theme.v2.color.neutral[500]};
                --border-color: ${theme.v2.color.neutral[400]};
                background-color: ${theme.v2.color.neutral[200]};
        `
				: ``}

    &:focus {
			border: 1px solid var(--border-color-focused);
		}
		caret-color: var(--text-color);
		color: var(--text-color);

		&::placeholder {
			color: var(--placeholder-color);
			opacity: 1; /* Firefox */
		}
	}

	${({ disabled }) =>
		disabled
			? `
            ${InputWrapper} {
                * {
                cursor: not-allowed !important;
                }
            }
    `
			: ``}

	${GroupWrapper} {
		width: 100%;
	}
	${InputWrapper}, input {
		width: 100%;
	}
	input {
		box-sizing: border-box;
	}
`;

export { Styled as StringInput };
